import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { FormValues, Text } from '@frontend/design-system';
import { FilterableCheckboxListItem } from '../../../../components/filterable-checkbox-list/use-filterable-checkbox-list';
import { CollapsibleFilterList } from '../../../appointment-list-filter-button/filter-drawer/collapsible-filter-list';
import { DetailedFiltersFormConfig, FilterState, toFormValues } from './filters-drawer';

export const AppliedFilters = ({
  normalizedValues,
  filterLists,
  seedValues,
}: {
  normalizedValues: FilterState;
  filterLists: {
    providers: FilterableCheckboxListItem[];
    operatories: FilterableCheckboxListItem[];
    services: FilterableCheckboxListItem[];
  };
  seedValues: (values: FormValues<DetailedFiltersFormConfig>) => void;
}) => {
  const { t } = useTranslation('schedule-settings');

  const appliedFilters = () => {
    const items = [];

    normalizedValues.providers.forEach((id) => {
      const label = filterLists.providers.find((p) => p.value === id)?.label || id;
      items.push({ type: 'provider', value: id, label });
    });

    normalizedValues.operatories.forEach((id) => {
      const label = filterLists.operatories.find((o) => o.value === id)?.label || id;
      items.push({ type: 'operatory', value: id, label });
    });

    normalizedValues.serviceTypes.forEach((id) => {
      const label = filterLists.services.find((s) => s.value === id)?.label || id;
      items.push({ type: 'service', value: id, label });
    });

    if (normalizedValues.startDate) {
      items.push({
        type: 'startDate',
        value: normalizedValues.startDate,
        label: `Start: ${normalizedValues.startDate}`,
      });
    }
    if (normalizedValues.endDate) {
      items.push({ type: 'endDate', value: normalizedValues.endDate, label: `End: ${normalizedValues.endDate}` });
    }

    if (normalizedValues.view && normalizedValues.view !== 'CURRENT') {
      items.push({ type: 'view', value: normalizedValues.view, label: normalizedValues.view });
    }
    if (normalizedValues.recurrenceType) {
      const recurrenceLabels = {
        SINGLE_OCCURRENCE: t('Single'),
        WEEKLY: t('Weekly'),
        CUSTOM: t('Custom'),
        SPECIFIC_DATE: t('Specific Date'),
      };
      items.push({
        type: 'recurrence',
        value: normalizedValues.recurrenceType,
        label: recurrenceLabels[normalizedValues.recurrenceType] || normalizedValues.recurrenceType,
      });
    }

    return items;
  };

  const handleRemoveFilter = (type: string, value: string) => {
    const updated = { ...normalizedValues };

    switch (type) {
      case 'provider':
        updated.providers = updated.providers.filter((v) => v !== value);
        break;
      case 'operatory':
        updated.operatories = updated.operatories.filter((v) => v !== value);
        break;
      case 'service':
        updated.serviceTypes = updated.serviceTypes.filter((v) => v !== value);
        break;
      case 'startDate':
        updated.startDate = '';
        break;
      case 'endDate':
        updated.endDate = '';
        break;
      case 'view':
        updated.view = undefined;
        break;
      case 'recurrence':
        updated.recurrenceType = undefined;
        break;
    }

    seedValues(toFormValues(updated));
  };

  if (!appliedFilters().length) return null;

  return (
    <div css={appliedFiltersContainerStyles}>
      <Text size='small' weight='bold'>
        {t('Applied Filters ({{count}})', { count: appliedFilters().length })}
      </Text>
      {appliedFilters().length > 0 ? (
        <CollapsibleFilterList selectedFilters={appliedFilters()} onRemove={handleRemoveFilter} maxRows={2} />
      ) : (
        <Text size='small'>{t('No filters applied')}</Text>
      )}
    </div>
  );
};

const appliedFiltersContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.colors.white,
  zIndex: theme.zIndex.low,
});
